const QualityAssuranceUnit4Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-cyan-900 mb-3 border-b-2 border-slate-200 pb-2">Unit IV: Complaints, Product Recalls & Document Maintenance</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            Documentation is the primary proof of compliance in the pharmaceutical industry. This unit covers the systematic tracking and management of market complaints, product recalls, and waste disposal, alongside the rigorous maintenance of critical documents like Master Formula Records (MFR) and Batch Formula Records (BFR).
        </p>
    </div>

    <!-- 2. Complaints and Recalls -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-cyan-800 mb-4 border-b border-slate-100 pb-2">1. Complaints, Returned Goods, Recalls & Waste Disposal</h3>
        
        <div class="space-y-4">
            <!-- Complaints -->
            <div>
                <h4 class="font-bold text-cyan-900 text-[15px] mb-1">A. Complaints and Evaluation</h4>
                <p class="text-[13px] text-slate-700 text-justify">
                    A complaint is any verbal or written expression of dissatisfaction regarding the quality, safety, identity, or efficacy of a marketed pharmaceutical product. 
                </p>
                <ul class="list-disc pl-5 mt-1 text-[12px] text-slate-600 space-y-1">
                    <li><strong>Categories:</strong> Quality complaints (e.g., chipped tablets, leaking bottles, bad odor) and Medical/Adverse effect complaints (e.g., unexpected side effects).</li>
                    <li><strong>Evaluation Procedure:</strong> Handled independently by the QA department. It includes examining the retained control samples of the respective batch, reviewing the Batch Manufacturing Record (BMR), investigating the root cause, and initiating **CAPA (Corrective and Preventive Action)**.</li>
                </ul>
            </div>

            <!-- Returned Goods & Recalls -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-cyan-50 p-4 rounded-lg border border-cyan-100 shadow-sm">
                    <h4 class="font-bold text-cyan-900 text-[14px] mb-1">B. Handling of Returned Goods & Recalls</h4>
                    <p class="text-[12px] text-slate-600 text-justify mb-2">
                        <strong>Handling of Returned Goods:</strong> Products returned from the market due to near-expiry, damage, or commercial reasons are placed in a dedicated **Quarantine Area**. QA evaluates their condition, checks documentation, and decides whether they should be reprocessed, salvaged, or destroyed.
                    </p>
                    <p class="text-[12px] text-slate-600 text-justify">
                        <strong>Product Recalls:</strong> The removal of a specific batch of a drug product from the market due to a confirmed or suspected defect that poses a risk to patients. A formal **SOP for Recalls** must detail the system to execute rapid withdrawals at any time.
                    </p>
                </div>

                <!-- Waste Disposal -->
                <div class="bg-red-50 p-4 rounded-lg border border-red-100 shadow-sm text-[12px] text-slate-700">
                    <h4 class="font-bold text-red-900 text-[14px] mb-1">C. Waste Disposal</h4>
                    <p class="text-justify">
                        Pharmaceutical waste (rejected batches, expired raw materials, toxic chemical effluents) must be carefully destroyed to protect public health and the environment.
                    </p>
                    <ul class="list-disc pl-4 mt-2 space-y-1 text-slate-600">
                        <li>All rejected materials must be rendered unusable (defaced or crushed) before final disposal to prevent illegal counterfeiting.</li>
                        <li>Methods include high-temperature incineration, secure chemical neutralization, and regulated landfills in compliance with Environmental Protection laws.</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

    <!-- 3. Document Maintenance -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-cyan-800 mb-4 border-b border-slate-100 pb-2">2. Document Maintenance in Pharmaceutical Industry</h3>
        <p class="text-[13px] text-slate-700 mb-3">
            In Good Manufacturing Practices (GMP), documentation is the key to ensuring full traceability. The fundamental rule is: <em>"If it is not written down, it did not happen."</em>
        </p>

        <!-- MFR vs BFR Table -->
        <h4 class="font-bold text-cyan-900 text-[15px] mb-2">Comparison: Master Formula Record (MFR) vs. Batch Formula Record (BFR)</h4>
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-6">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-cyan-100 text-cyan-900">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/4">Feature</th>
                        <th class="px-4 py-2.5 font-bold w-3/8">Master Formula Record (MFR)</th>
                        <th class="px-4 py-2.5 font-bold w-3/8">Batch Formula Record (BFR)</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-cyan-900">Definition</td>
                        <td class="px-4 py-3">A standard reference document prepared by R&D that provides the official blueprint for manufacturing a specific product.</td>
                        <td class="px-4 py-3">A processing record issued for <strong>each individual batch</strong> during production, copied directly from the approved MFR.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-cyan-900">Nature</td>
                        <td class="px-4 py-3">Static reference template (remains unchanged unless officially revised).</td>
                        <td class="px-4 py-3">Dynamic log filled in real-time by operators as steps are executed.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-cyan-900">Core Contents</td>
                        <td class="px-4 py-3">Product design specifications, names/quantities of all starting materials, theoretical yield calculation, detailed process parameters, and packaging designs.</td>
                        <td class="px-4 py-3">Batch number, exact weights measured, time logs for every step, signatures of executing operators, equipment cleaning logs, and in-process QC testing results.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- SOP, Quality Audit Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px] text-slate-700">
            <!-- SOP -->
            <div class="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <h4 class="font-bold text-cyan-900 text-[14px] mb-1">Standard Operating Procedures (SOPs)</h4>
                <p class="text-[12px] text-slate-600 text-justify">
                    SOPs are written, authorized, step-by-step instructions that describe how to perform routine operations consistently. They minimize human variance and maintain operational standard uniformity across operations like equipment handling, sanitization, sampling, and analytical testing.
                </p>
            </div>
            
            <!-- Quality Audit & Review -->
            <div class="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <h4 class="font-bold text-cyan-900 text-[14px] mb-1">Quality Audit & Quality Review</h4>
                <p class="text-[12px] text-slate-600 text-justify">
                    <strong>Quality Audit:</strong> A systematic, independent examination to check whether product operations comply with GMP. It includes internal self-inspections and external regulatory audits.
                </p>
                <p class="text-[12px] text-slate-600 text-justify mt-2">
                    <strong>Annual Product Quality Review (APQR):</strong> A mandatory yearly review of all manufactured product batches to verify process consistency, identify hidden trends, and validate specifications.
                </p>
            </div>
        </div>

        <!-- Distribution & Documentation -->
        <div class="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-[13px] text-slate-700">
            <h4 class="font-bold text-blue-900 text-[14px] mb-1">Quality Documentation & Distribution Records</h4>
            <p class="text-[12px] text-slate-600 text-justify">
                <strong>Distribution Records:</strong> A detailed logistical record capturing the invoice numbers, batch allocations, destination addresses, and shipment dates for all products dispatched from the warehouse. This ensures that in the event of an emergency recall, specific defective batches can be located instantly in the market supply chain.
            </p>
        </div>
    </div>

</div>
`;

export default QualityAssuranceUnit4Notes;